import { t } from "@lingui/macro";
import * as Sentry from "@sentry/react-native";
import * as Updates from "expo-updates";
import { Platform } from "react-native";

import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";

import { prefetchExpoUpdateAsync } from "./prefetchExpoUpdateAsync";

export type CheckForUpdatesOutcome =
  | "update_applied"
  | "no_update"
  | "error"
  | "unsupported";

export type CheckForUpdatesResult = {
  outcome: CheckForUpdatesOutcome;
  message?: string;
};

export const useCheckForUpdatesFunction = () => {
  const { showToast } = useToast();

  const checkForUpdates = async (): Promise<CheckForUpdatesResult> => {
    if (Platform.OS !== "web") {
      try {
        const update = await prefetchExpoUpdateAsync();
        if (update.outcome === "update_downloaded") {
          // ... notify user of update ...
          showToast({
            description: t`Boy Howdy! We got some updates coming in hot! Just a moment while we apply those!`,
            title: t`New Update`,
            semanticStyle: "success",
            toastId: "new-updates-toast",
          });
          // Load update after waiting 1 second
          await new Promise((resolve) => setTimeout(resolve, 2000));
          await Updates.reloadAsync();
          return { outcome: "update_applied" };
        }
        return {
          outcome: "no_update",
          message: t`We couldn't find an update right now. Please try again, or update from the app store if available.`,
        };
      } catch (e) {
        Sentry.captureException(e);
        return {
          outcome: "error",
          message: t`There was a problem checking for updates. Please try again in a moment.`,
        };
      }
    }
    return {
      outcome: "unsupported",
    };
  };
  return checkForUpdates;
};
